import { createEffect, createSignal, onCleanup } from 'solid-js';
import { stripBasePath } from '@/config/langPath';
import { appConfig } from '@/config/env';
import { useLocation } from '@solidjs/router';
import { useFunnelRuntime } from '@/modules/runtime';
import { documentTitleFor, type TitleTranslate } from './documentTitle';
import { capturePageview } from '@/modules/analytics/pageview';
import { screenSlugFromPath } from '@/modules/funnel/screenRoute';

/**
 * Router root only. Re-runs on every route change and once the locales land, so a title rendered from the
 * fallbacks before `translations.init()` resolves is replaced by the translated one.
 */
export const useDocumentTitle = (): void => {
  const location = useLocation();
  const { translations } = useFunnelRuntime();
  const [revision, setRevision] = createSignal(0);
  const onLoaded = () => setRevision((value) => value + 1);
  translations.addListener('translationLoaded', onLoaded);
  onCleanup(() => translations.removeListener('translationLoaded', onLoaded));

  const translate: TitleTranslate = (keys) => {
    try {
      return translations.translate(keys, {})?.translation;
    } catch {
      // i18next is not initialised yet: the fallbacks stand until `translationLoaded` fires.
      return undefined;
    }
  };

  createEffect(() => {
    revision();
    document.title = documentTitleFor(stripBasePath(location.pathname, appConfig.basePath), translate);
  });

  // One $pageview per route; quiz screens report their own (FunnelPage), the router never sees them.
  createEffect(() => {
    const pathname = location.pathname;
    const isQuiz = screenSlugFromPath(pathname, appConfig.basePath) !== null;
    if (!isQuiz && stripBasePath(pathname, appConfig.basePath) !== '/') capturePageview(pathname);
  });
};
